import axios from 'axios';
import { toast } from '../components/Toast';
import { getErrorMessage } from '../types/api-error';

// 开发环境走 vite proxy（只用前缀），生产环境拼接完整地址直连
const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').trim();
const apiPrefix = (import.meta.env.VITE_API_PREFIX || '/admin').trim();
const baseURL = import.meta.env.PROD ? apiBaseUrl + apiPrefix : apiPrefix;

// 生产环境若拼接出异常地址（如包含 undefined），立即告警，避免请求发错导致白屏
if (import.meta.env.PROD && baseURL.includes('undefined')) {
  console.error(
    '[api/client] 生产环境 API baseURL 异常，请检查环境变量 VITE_API_BASE_URL 和 VITE_API_PREFIX。当前 baseURL =',
    baseURL
  );
}

export const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 后端统一响应包装 { code, message, data }
interface WrappedResponse<T = unknown> {
  code: number;
  message: string;
  data?: T;
}

function isWrapped(data: unknown): data is WrappedResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'code' in data &&
    'message' in data
  );
}

// 统一处理业务层 code 非零（后端 200 但 code≠0 的业务错误）
api.interceptors.response.use(
  (response) => {
    const body = response.data;
    console.log('[axios:ok]', response.config.url, 'status:', response.status, 'body keys:', body && typeof body === 'object' ? Object.keys(body) : typeof body, 'body:', body);
    if (!isWrapped(body)) {
      console.log('[axios:ok] 非包装格式，透传');
      return response;
    }
    console.log('[axios:ok] 包装格式，code:', body.code);

    // 业务层 401：未登录，跳转登录页
    if (body.code === 401) {
      console.log('[axios:ok] 业务 401，pathname:', window.location.pathname);
      if (window.location.pathname !== '/login') {
        console.log('[axios:ok] → 跳转 /login');
        window.location.href = '/login';
      }
      return Promise.reject(
        Object.assign(new Error(body.message || '未登录'), {
          response: { status: 401, data: body },
        }),
      );
    }

    // 其他业务错误
    if (body.code !== 0) {
      console.log('[axios:ok] 业务错误 code:', body.code, 'message:', body.message);
      toast(body.message || '请求失败', 'error');
      return Promise.reject(
        Object.assign(new Error(body.message || '请求失败'), {
          response: { status: body.code, data: body },
        }),
      );
    }

    // code === 0：解包，用 data 替换 response.data
    console.log('[axios:ok] code=0 解包，data:', body.data, 'data type:', typeof body.data, 'isArray:', Array.isArray(body.data));
    response.data = body.data;
    return response;
  },
  (error) => {
    console.log('[axios:err]', error.config?.url, 'status:', error.response?.status, 'data:', error.response?.data);
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      console.log('[axios:err] HTTP 401 → 跳转 /login');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    console.log('[axios:err] 显示 toast');
    toast(getErrorMessage(error, '请求失败，请稍后重试'), 'error');
    return Promise.reject(error);
  },
);
